import { useEffect, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import type { Producto, UnidadVenta } from '@abastece/core';
import { db } from '../db/dexie.js';
import { ensenarCodigo, unidadesDe } from '../lib/catalogo.js';
import { cantidadLimpia, fotoAbierta, parecidos, useCodigoAbierto } from '../lib/catalogos-abiertos.js';
import { enPractica } from '../lib/practica.js';
import { usePuede } from '../lib/sesion.js';
import { useT } from '../i18n/index.js';
import { useNombreProducto } from '../i18n/productos.js';
import { EnsenarCodigo } from './EnsenarCodigo.js';
import { Ico } from './Iconos.js';
import { Miniatura } from './Miniatura.js';

/**
 * What the open catalogues say a code nobody in the shop has is, in the sheet that asks about it.
 *
 * The product as the catalogue has it (its photo, name, brand and size, and whose word it is),
 * then the shop's own products that look like it (`parecidos`). One of those tapped takes the code,
 * on the presentation it is printed on when there are several, and is what was scanned: no
 * duplicate made, nothing typed. Making a new product instead is the sheet's «Crear producto»,
 * which fills the form in from the same answer.
 *
 * Says nothing when there is nothing to ask, a code that is not a product's GTIN. Otherwise it
 * always says something, so that no answer is never mistaken for no question: that nobody knows
 * it, that nobody could be asked, or, in the practice shop, which talks to no server, that only
 * the real shop asks (the owner scanned there and took the silence for a fault, 2026-10-02).
 */
export function SugerenciaAbierta({
  codigo,
  onElegido,
  conPista = true,
}: {
  codigo: string;
  /** One of the shop's products, now carrying the code; with the presentation, when it went on one. */
  onElegido: (p: Producto, unidad?: UnidadVenta) => void;
  /** That «Crear producto», beside it, fills the new product in from this: not where the form is open already. */
  conPista?: boolean;
}) {
  const t = useT();
  const nombre = useNombreProducto();
  const puedeEnsenar = usePuede('editar_productos');
  const consulta = useCodigoAbierto(codigo);
  const productos = useLiveQuery(() => db.productos.toArray(), [], []);
  const unidades = useLiveQuery(() => db.unidades.toArray(), [], []);
  /** A suggestion sold in several presentations: which one the code is printed on. */
  const [eligiendo, setEligiendo] = useState<Producto | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (consulta.estado === 'nada') return null;
  if (consulta.estado === 'apagado') {
    return enPractica() ? <Nota texto={t('abiertos.enPractica')} /> : null;
  }
  if (consulta.estado === 'buscando') {
    return (
      <p role="status" className="flex min-h-14 items-center gap-2 text-sm text-slate-400">
        <Ico icono="buscar" className="size-4 shrink-0 animate-pulse" />
        {t('abiertos.buscando')}
      </p>
    );
  }
  const d = consulta.datos;
  if (!d?.encontrado) return <Nota texto={t(!d || d.ocupado ? 'abiertos.sinRespuesta' : 'abiertos.nadie')} />;

  async function elegir(p: Producto) {
    const propias = unidadesDe(unidades, p.id);
    if (propias.length > 1) {
      setEligiendo(p);
      return;
    }
    setError(null);
    setOcupado(true);
    try {
      onElegido(p, await ensenarCodigo(p.id, undefined, propias, codigo));
    } catch (e) {
      setError(e instanceof Error ? e.message : t('producto.noSePudoGuardar'));
      setOcupado(false);
    }
  }

  const candidatos = puedeEnsenar ? parecidos(productos, d) : [];
  const medida = cantidadLimpia(d.cantidad) ?? d.cantidad;
  return (
    <div className="flex flex-col gap-3" data-sugerencia-abierta>
      <div className="flex items-center gap-3 rounded-2xl bg-slate-800/60 p-3 ring-1 ring-white/5 ring-inset">
        <FotoAbierta codigo={codigo} hay={Boolean(d.foto)} />
        <div className="min-w-0 flex-1">
          <p className="font-medium break-words">{d.nombre ?? d.marca ?? codigo}</p>
          {(d.marca || medida) && (
            <p className="text-sm break-words text-slate-400">{[d.marca, medida].filter(Boolean).join(' · ')}</p>
          )}
          <p className="text-xs text-slate-500">{t('abiertos.segun', { fuente: d.fuente ?? '' })}</p>
        </div>
      </div>
      {puedeEnsenar && conPista && <p className="text-sm text-slate-400">{t('abiertos.alCrear')}</p>}

      {eligiendo ? (
        <EnsenarCodigo codigo={codigo} producto={eligiendo} onListo={onElegido} />
      ) : (
        candidatos.length > 0 && (
          <div>
            <p className="mb-2 text-sm font-medium">{t('abiertos.suyos')}</p>
            <ul className="flex flex-col gap-2">
              {candidatos.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    className="tarjeta-accion flex w-full items-center gap-3 p-3 text-left"
                    disabled={ocupado}
                    onClick={() => void elegir(p)}
                    data-sin-icono="fila"
                  >
                    <Miniatura producto={p} tamano="md" />
                    <span className="min-w-0 flex-1 font-medium break-words">{nombre(p)}</span>
                    <Ico icono="adelante" className="size-4 shrink-0 text-slate-500" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )
      )}
      {error && <p className="text-sm text-rose-400">{error}</p>}
    </div>
  );
}

/** What the catalogues could not say, in a line of its own: easy to read, not to be taken for a fault. */
function Nota({ texto }: { texto: string }) {
  return (
    <p role="status" className="flex items-start gap-2 rounded-xl bg-slate-800/60 px-3 py-2.5 text-sm text-slate-300" data-sugerencia-abierta>
      <Ico icono="buscar" className="mt-0.5 size-4 shrink-0 text-slate-400" />
      {texto}
    </p>
  );
}

/** The catalogue's photo of the front, brought through the shop's server; a plain square while it comes. */
function FotoAbierta({ codigo, hay }: { codigo: string; hay: boolean }) {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!hay) return;
    let vigente = true;
    let propia: string | null = null;
    void fotoAbierta(codigo).then((blob) => {
      if (!vigente || !blob) return;
      propia = URL.createObjectURL(blob);
      setUrl(propia);
    });
    return () => {
      vigente = false;
      if (propia) URL.revokeObjectURL(propia);
    };
  }, [codigo, hay]);
  return url ? (
    <img src={url} alt="" className="size-14 shrink-0 rounded-xl bg-white object-contain" />
  ) : (
    <span className="chip chip-neutro size-14 shrink-0 rounded-xl">
      <Ico icono="productos" className="size-6" />
    </span>
  );
}
